import React, { useMemo } from 'react';
import { Table, Form } from 'react-bootstrap';
import { calculateTSSWithTerrain } from '../../utils/tssUtils';
import { useTranslation } from '../../i18n';

const formatNumber = (v, decimals = 0) => {
  if (v === null || v === undefined || v === '') return '';
  const n = Number(v);
  if (!Number.isFinite(n)) return '';
  return decimals > 0 ? n.toFixed(decimals) : String(Math.round(n));
};

const formatDurationHMS = (raw) => {
  if (raw === null || raw === undefined || raw === '') return '';
  if (typeof raw === 'string' && raw.includes(':')) return raw;
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return '';
  const seconds = n > 1_000_000 ? Math.floor(n / 1000) : Math.floor(n);
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const pad = (x) => String(x).padStart(2, '0');
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
};

const hmsToSeconds = (raw) => {
  if (raw === null || raw === undefined || raw === '') return 0;
  if (typeof raw === 'number') {
    if (!Number.isFinite(raw) || raw <= 0) return 0;
    return raw > 1_000_000 ? raw / 1000 : raw;
  }
  if (typeof raw === 'string' && raw.includes(':')) {
    const parts = raw.trim().split(':').map((p) => parseInt(p, 10));
    if (!parts.length || parts.some((x) => !Number.isFinite(x))) return 0;
    if (parts.length === 2) { const [mm, ss] = parts; return mm * 60 + ss; }
    if (parts.length === 3) { const [hh, mm, ss] = parts; return hh * 3600 + mm * 60 + ss; }
  }
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return n > 1_000_000 ? n / 1000 : n;
};

const resolveVP = (runningThresholdPaceSec = null) => {
  const win = typeof window !== 'undefined' ? window : null;
  let vpFromWindow = win?.__TP_THRESHOLD_PACE__ ?? null;
  let vpFromStorage = null;
  try { vpFromStorage = win?.localStorage?.getItem('tp_threshold_pace') ?? null; } catch (_) {}
  vpFromWindow = vpFromWindow && String(vpFromWindow).trim().length > 0 ? String(vpFromWindow).trim() : null;
  vpFromStorage = vpFromStorage && String(vpFromStorage).trim().length > 0 ? String(vpFromStorage).trim() : null;
  const vp = vpFromWindow || vpFromStorage || null;
  if (vpFromWindow) { try { win?.localStorage?.setItem('tp_threshold_pace', vpFromWindow); } catch (_) {} }
  return { vp, runningThresholdPaceSec };
};

const ActivityPlannedCompletedTable = ({ planned, completed, setPlanned, runningThresholdPaceSec = null }) => {
  const { t } = useTranslation();

  const computedTSS = useMemo(() => {
    const completedTssNum = Number(completed?.tss);
    if (Number.isFinite(completedTssNum) && completedTssNum > 0) {
      return { tss: completedTssNum, source: 'completed' };
    }
    const durationRaw = completed?.duration ?? completed?.duracion ?? null;
    const durationSec = hmsToSeconds(durationRaw);
    const avgPace = completed?.avgPace ?? completed?.ritmo_medio ?? completed?.pace ?? '';
    if (!durationSec || !avgPace || !runningThresholdPaceSec) return { tss: NaN, source: 'none' };
    const activityData = {
      activity: { totalTimerTime: durationSec },
      sessions: { sport: 'running', avgPace },
      user: { runningThresholdPaceSec },
      zones_target: {},
    };
    const res = calculateTSSWithTerrain(activityData);
    return { tss: res?.tss, source: 'profile', message: res?.message };
  }, [completed, runningThresholdPaceSec]);

  const finalTssString = useMemo(() => {
    const n = Number(computedTSS?.tss);
    return Number.isFinite(n) ? n.toFixed(2) : '';
  }, [computedTSS]);

  const fieldMapping = {
    duracion: formatDurationHMS(completed?.duration ?? completed?.duracion),
    distancia: formatNumber(completed?.distance ?? completed?.distancia, 2),
    ritmo_medio: completed?.avgPace ?? completed?.ritmo_medio ?? '',
    calorias: formatNumber(completed?.calories > 0 ? completed?.calories : completed?.calorias, 0),
    desnivel_positivo: formatNumber(completed?.totalAscent ?? completed?.desnivel_positivo, 0),
    tte: completed?.tte ?? '',
    tea: completed?.tea ?? '',
    tss: finalTssString || '',
  };

  const fields = [
    { field: 'duracion',          label: t('activityDetails.durationField'),      unit: 'h:m:s'  },
    { field: 'distancia',         label: t('activityDetails.distanceField'),      unit: 'km'     },
    { field: 'ritmo_medio',       label: t('activityDetails.avgPaceField'),       unit: 'min/km' },
    { field: 'calorias',          label: t('activityDetails.caloriesField'),      unit: 'kcal'   },
    { field: 'desnivel_positivo', label: t('activityDetails.elevationGainField'), unit: 'm'      },
    { field: 'tte',               label: 'TTE',                                   unit: ''       },
    { field: 'tea',               label: 'TEA',                                   unit: ''       },
    { field: 'tss',               label: 'TSS',                                   unit: ''       },
  ];

  return (
    <div>
      
      <Table striped bordered hover>
        <thead style={{ backgroundColor: '#193255', color: '#fff' }}>
          <tr>
            <th>{t('activityDetails.metricLabel')}</th>
            <th>{t('activityDetails.plannedLabel')}</th>
            <th>{t('activityDetails.completedLabel')}</th>
            <th>{t('activityDetails.unitLabel')}</th>
          </tr>
        </thead>
        <tbody>
          {fields.map(({ field, label, unit }) => (
            <tr key={field}>
              <td>{label}</td>
              <td>
                <Form.Control
                  type="text"
                  value={planned?.[field] ?? ''}
                  onChange={(e) => setPlanned({ ...planned, [field]: e.target.value })}
                />
              </td>
              <td>
                <Form.Control type="text" value={fieldMapping[field] ?? ''} readOnly />
              </td>
              <td>{unit}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

export default ActivityPlannedCompletedTable;
